import { Dispatch, SetStateAction, useEffect } from "react";
import { useQuery } from "react-query";

import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";

import { apis, useZusParams } from "..";
import { TRANSLATE_PREFIX } from "../../..";
import { CustomPagination } from "../../../../../../components/CustomPagination";
import LoadingDialog from "../../../../../../components/LoadingDialog";
import { NoRowsOverlay } from "../../../../../../components/NoRowsOverlay";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../../../../hooks/useColumnHide";
import { dataGridDefaults } from "../../../../../../utils/constant";
import { useGenGridCol } from "../../../../../../utils/HelperComp";
import useTabTranslation from "src/hooks/useTabTranslation";
import cleanParams from "../../../helpers/cleanParams";
import { TAB_TRANSLTE_PREFIX } from "../constants";

export default function Table(props: {
  listMapping: (which: "key" | "name", array: any[], config?: any) => any[][];
  setCount: Dispatch<SetStateAction<number>>;
}) {
  const { t, translate } = useTabTranslation({
    featurePrefix: TRANSLATE_PREFIX,
    tabPrefix: TAB_TRANSLTE_PREFIX,
  });
  const zusParams = useZusParams();
  const { listMapping, setCount } = props;
  const listRes = useQuery({
    queryKey: [TAB_TRANSLTE_PREFIX, zusParams.body, zusParams.refetchCounter],
    queryFn: () => apis.getAll(cleanParams(zusParams.body)),
  });
  const { rows = [], count = 0 } =
    (listRes.data as any as { rows: any[]; count: number }) || {};
  const content: GridRowsProp = listMapping("key", rows, {
    t,
    translate,
  });

  useEffect(() => setCount(count), [listRes]);

  const columns: GridColDef[] = [
    useGenGridCol("chainName", t("chainName"), { minWidth: 150 }),
    useGenGridCol("currency", t("currency")),
    useGenGridCol("groupName", t("groupName"), { minWidth: 150 }),
    useGenGridCol("riskType", t("riskType"), { minWidth: 150 }),
    useGenGridCol("walletDisplayName", t("walletDisplayName"), {
      minWidth: 150,
    }),
    useGenGridCol("recipientAddress", t("recipientAddress"), { minWidth: 150 }),
    useGenGridCol("amount", t("amount"), { minWidth: 150 }),
    useGenGridCol("activeStartTime", t("activeStartTime"), { minWidth: 150 }),
    useGenGridCol("activeEndTime", t("activeEndTime"), {
      minWidth: 150,
    }),
  ];

  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    columns,
    getColumnHideConfig(),
  );
  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;
  return (
    <>
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        rowCount={count}
        columns={columns}
        page={zusParams.body.page}
        onPageChange={zusParams.setPage}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}
